<template>
  <div v-if="card">
    <section v-if="display === 'valueIntro'" class="intro">
      <h4>Let's figure out what your card is worth</h4>
      <p>Card Ladder offers a few different ways of estimating the value of your card. Which would you prefer?</p>

      <div class="cta-options">
        <div v-for="(option, i) in estimateOptions" :key="i">
          <cta-card
            :icon="option.icon"
            :title="option.title"
            :description="option.description"
            :active="activeOption && activeOption === option.id"
            @click="selectEstimateOption(option)"
          />

          <external-link v-if="option.learnMoreLink" :href="option.learnMoreLink">
            <span>Learn More</span>
          </external-link>
        </div>
      </div>
    </section>

    <section v-if="display === 'valueGradedCardAutomatic'">
      <div class="intro">
        <h4>Help us figure out which card this is</h4>
        <p>Input the graded card cert # and the grader.</p>
      </div>

      <div class="form">
        <text-input
          v-model="certInput"
          label="Cert #"
          placeholder="63444200"
        />

        <select-input
          v-model="certGraderInput"
          label="Grader"
          :options="graderOptions"
        />
      </div>

      <portal to="collection-edit-card-modal-footer">
        <align small-gap>
          <btn
            :disabled="!certInput || !certGraderInput"
            :loading="saving"
            @click="fetchGradedCardInfo"
          >
            Save
          </btn>

          <btn kind="secondary" @click="goBack">
            Back
          </btn>
        </align>
      </portal>
    </section>

    <section v-if="display === 'valuePlayer'">
      <div class="intro">
        <h4>Help us figure out the player on this card</h4>
        <p>Search for the player below, and once selected, we'll do the rest. If you can't find the player, go back and try the another estimation method.</p>
      </div>

      <div class="form">
        <player-search @select="setPlayer" />
      </div>

      <portal to="collection-edit-card-modal-footer">
        <align small-gap>
          <btn kind="secondary" @click="goBack">
            Back
          </btn>
        </align>
      </portal>
    </section>

    <section v-if="display === 'valueMultipliers'">
      <div class="intro">
        <h4>Select one or more cards</h4>
        <p>We will help determine the ratio of value against your card.</p>
      </div>

      <div class="form">
        <card-multipliers
          v-model="card.multipliers"
          :card="card"
        />
      </div>

      <portal to="collection-edit-card-modal-footer">
        <align small-gap>
          <btn
            :disabled="!hasCompleteMultiplier"
            :loading="saving"
            @click="submitMultipliers"
          >
            Save
          </btn>

          <btn kind="secondary" @click="goBack">
            Back
          </btn>
        </align>
      </portal>
    </section>

    <section v-if="display === 'valueAdvanced'" class="advanced">
      <div class="intro">
        <h4>Index + Manual Sale</h4>
        <p>In order to estimate the value of your card we need to collect three pieces of information. The index you'd like to use and a known value at a given point in time. Given those three things we can map out the estimated value over time, tracking against the index you selected.</p>
      </div>

      <div class="grid">
        <section>
          <h5>First, let's choose an index</h5>

          <index-search
            @selectPlayer="setPlayer"
            @selectIndex="setIndex"
          />

          <div v-if="selectedIndex" class="index-list-container">
            <index-list-item :index="selectedIndex" />
          </div>

          <br />
          <h5>Next, let's use a past sale for the value and date</h5>
          <p>This can be any sale that you think is related to your card. Ideally you would find an exact match but you can be creative. Once you choose a sale we will fill out the value and date fields for you. Feel free to adjust either.</p>

          <sales-history-search @select="handleSaleSelected" />

          <br />

          <p>You can also enter in anything you want here. If you bought the card raw and got it graded PSA 10, feel free to enter in the value you think it is today and we'll update it for you going forward.</p>

          <div class="input-group">
            <currency-input
              v-model="card.playerIndexPrice"
              label="Value"
            />

            <date-input
              v-model="card.playerIndexDate"
              label="Date"
            />
          </div>
        </section>

        <section>
          <div v-if="card" class="checks">
            <align :class="{ success: Boolean(card.playerIndexId || card.otherIndexId) }">
              <icon class="check" name="check_circle" />
              <h5 class="secondary-font">
                Index
              </h5>
            </align>

            <align :class="{ success: Boolean(card.playerIndexPrice) }">
              <icon class="check" name="check_circle" />
              <h5 class="secondary-font">
                Value
              </h5>
            </align>

            <align :class="{ success: Boolean(card.playerIndexDate) }">
              <icon class="check" name="check_circle" />
              <h5 class="secondary-font">
                Date
              </h5>
            </align>
          </div>

          <card-list-item :card="card" />

          <div v-if="guideValue" class="value-estimate">
            <value-display
              estimated
              size="large"
              :value="$filters.currency(guideValue.value, { full: true })"
            />

            <div v-if="guideValue.description" class="description">
              {{ guideValue.description }}
            </div>
          </div>
        </section>
      </div>

      <portal to="collection-edit-card-modal-footer">
        <align small-gap>
          <btn
            :disabled="!hasCompleteAdvanced"
            :loading="saving"
            @click="submitAdvanced"
          >
            Save
          </btn>

          <btn kind="secondary" @click="goBack">
            Back
          </btn>
        </align>
      </portal>
    </section>

    <section v-if="display === 'valueLastSale'" class="lastsale">
      <div class="intro">
        <h4>Custom Sales Query</h4>
        <p>In order to determine the value using this method we need to construct a query on our Sales History database that retrieves only your card. Then we will use the most recent sale to determine the value of your card. You may also select an index to project the most recent sale's value forward if you'd like. Each night we automatically pull in the latest sales of your card and update the value of your card.</p>
      </div>

      <div class="grid">
        <section>
          <div>
            <pill-dropdown
              v-model="lastSoldOptionsValue"
              align="left"
              :options="lastSoldOptions"
            />
          </div>

          <br />

          <div v-show="lastSoldOptionsValue === 'index'">
            <index-search
              @selectPlayer="setPlayer"
              @selectIndex="setIndex"
            />

            <div v-if="selectedIndex" class="index-list-container">
              <index-list-item :index="selectedIndex" />
            </div>
          </div>

          <br />

          <p>Read <a href="https://cardladder.zendesk.com/hc/en-us/articles/12047786944663-12-Rules-for-Writing-Search-Queries" target="_blank">this document</a> written by our team to help you build an accurate query.</p>

          <text-area-input v-model="card.ebayQuery" label="Query" />

          <div v-if="card.ebayQuery">
            <align class="results-header" small-gap>
              <h5>Search Results</h5>

              <icon-button
                as="router-link"
                target="_blank"
                :to="salesHistoryLink"
                icon="open_in_new"
                size="16"
                tooltip="Open in new tab"
              />
            </align>

            <div v-if="loadingSales" class="loading-container">
              <spinner inline />
            </div>

            <div v-else-if="sales.length > 0" class="sales-container">
              <sales-list-item
                v-for="item in sales"
                :key="item.itemId"
                :item="item"
                as="a"
                split
                :href="item.url"
                target="_blank"
              />
            </div>
          </div>
        </section>

        <section>
          <div v-if="card" class="checks">
            <align :class="{ success: Boolean(card.ebayQuery && sales.length) }">
              <icon class="check" name="check_circle" />
              <h5 class="secondary-font">
                Query
              </h5>
            </align>

            <align :class="{ success: lastSoldOptionsValue !== 'index' || Boolean(card.playerIndexId || card.otherIndexId) }">
              <icon class="check" name="check_circle" />
              <h5 class="secondary-font">
                Value Method
              </h5>
            </align>
          </div>

          <card-list-item :card="card" />

          <div v-if="guideValue" class="value-estimate">
            <value-display
              estimated
              size="large"
              :value="$filters.currency(guideValue.value, { full: true })"
            />

            <div class="description">
              {{ guideValue.description }}
            </div>
          </div>
        </section>
      </div>

      <portal to="collection-edit-card-modal-footer">
        <align small-gap>
          <btn
            :disabled="!hasCompleteLastSale || !sales.length || (lastSoldOptionsValue === 'index' && !card.playerIndexId && !card.otherIndexId)"
            :loading="saving"
            @click="submitLastSale"
          >
            Save
          </btn>

          <btn kind="secondary" @click="goBack">
            Back
          </btn>
        </align>
      </portal>
    </section>

    <section v-if="display === 'valueManual'">
      <div class="intro">
        <h4>Manual value estimation</h4>
        <p>Enter what you think the card is worth. Instead of automatically estimating the value of your card, Card Ladder will use the value you enter here.</p>
      </div>

      <div class="form">
        <currency-input
          v-model="card.estimatedCurrentValue"
          label="Estimated Value"
          placeholder="2000"
        />
      </div>

      <portal to="collection-edit-card-modal-footer">
        <align small-gap>
          <btn
            :disabled="!card.estimatedCurrentValue"
            :loading="saving"
            @click="submitManual"
          >
            Save
          </btn>

          <btn kind="secondary" @click="goBack">
            Back
          </btn>
        </align>
      </portal>
    </section>

    <section v-if="display === 'loading'">
      <div class="intro">
        <h4>Estimating the value...</h4><br />
        <spinner inline />
      </div>
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import debounce from 'lodash.debounce';
import moment from 'moment';
import firebase from 'firebase/compat/app';
import * as Sentry from '@sentry/vue';

import {
  cardService,
  authService,
  playerService,
  indexService,
  searchService,
} from '@/services';

import { getCollectionCardToSave, getGuideValue } from '@cardladder/shared/utils/collection.util';
import { getDailySalesFromSalesSearch } from '@cardladder/shared/utils/sales.util';
import { COLLECTION_CARD_ESTIMATE_OPTIONS, GRADING_COMPANY_UPLOAD_OPTIONS } from '@cardladder/shared/constants';

import CardListItem from '@cardladder/shared/components/CardListItem';

import CardMultipliers from '@/cards/CardMultipliers';
import PlayerSearch from '@/cards/PlayerSearch';
import IndexSearch from '@/components/IndexSearch';
import SalesHistorySearch from '@/cards/SalesHistorySearch';
import IndexListItem from '@cardladder/shared/components/IndexListItem';
import SalesListItem from '@cardladder/shared/components/SalesListItem';
import ValueDisplay from '@cardladder/shared/components/ValueDisplay';

export default {
  components: {
    CardListItem,
    CardMultipliers,
    IndexSearch,
    PlayerSearch,
    SalesHistorySearch,
    IndexListItem,
    SalesListItem,
    ValueDisplay,
  },

  props: {
    collectionId: String,
    value: Object,
    display: String,
  },

  data() {
    return {
      card: this.value,
      player: null,
      index: null,
      saving: false,
      loadingSales: false,
      sales: [],
      certInput: '',
      certGraderInput: 'psa',
      estimateOptions: COLLECTION_CARD_ESTIMATE_OPTIONS,
      graderOptions: GRADING_COMPANY_UPLOAD_OPTIONS,
      lastSoldOptionsValue: 'index',
      lastSoldOptions: [
        { value: 'index', label: 'Use an index + last sold to track value' },
        { value: 'lastsold', label: 'Use just the last sold price to track value' },
      ],
    };
  },

  mounted() {
    this.debounce_fetchSales = debounce(this.fetchSales, 1000);

    if (this.display === 'valueLastSale') {
      this.chooseLastSale();
    }
  },

  watch: {
    card: {
      handler(value) {
        this.$emit('input', value);
      },
      deep: true,
    },

    value: {
      handler(value) {
        this.card = value;
      },
      deep: true,
    },

    'card.ebayQuery': {
      handler() {
        this.debounce_fetchSales();
      },
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    activeOption() {
      if (this.hasCompletedGradedCardInfo && this.card?.useGradedCardAutomatic) {
        return 'gradedcardautomatic';
      } else if (this.hasCompleteLastSale && this.card?.useSalesForValue) {
        return 'lastsale';
      } else if (this.hasCompleteAdvanced) {
        return 'advanced';
      } else if (this.hasCompleteMultiplier) {
        return 'multipliers';
      } else if (this.card?.playerIndexId) {
        return 'playerindex';
      } else if (this.card?.estimatedCurrentValue) {
        return 'manual';
      }

      return null;
    },

    selectedIndex() {
      return this.player || this.index;
    },

    hasCompleteMultiplier() {
      return Boolean(this.card?.multipliers?.some(({ card, value }) => Boolean(card && value)));
    },

    hasCompleteAdvanced() {
      return Boolean(this.card?.playerIndexPrice && this.card?.playerIndexDate && (this.card?.playerIndexId || this.card?.otherIndexId));
    },

    hasCompleteLastSale() {
      return Boolean(this.card?.ebayQuery);
    },

    hasCompletedGradedCardInfo() {
      return this.card?.profileId && this.card?.grade && this.card?.grader;
    },

    dailySales() {
      return getDailySalesFromSalesSearch(this.sales).dailySales;
    },

    guideValue() {
      if (!this.card || !this.selectedIndex || !this.selectedIndex.dailyIndexTotal) {
        return null;
      }

      let indexDate = null;
      let indexPrice = null;

      if (this.hasCompleteLastSale && this.sales.length) {
        indexDate = this.sales[0].date;
        indexPrice = this.dailySales[moment(indexDate).utcOffset(-7).format('MM/DD/YYYY')].p;

        if (this.lastSoldOptionsValue === 'lastsold') {
          return { value: indexPrice };
        }
      } else if (this.hasCompleteAdvanced) {
        indexDate = this.card.playerIndexDate;
        indexPrice = this.card.playerIndexPrice;
      }

      if (!indexDate || !indexPrice) {
        return null;
      }

      const guideValue = getGuideValue(this.selectedIndex, indexDate, indexPrice);

      if (!guideValue || !guideValue.value) {
        return null;
      }

      return guideValue;
    },

    salesHistoryLink() {
      return `/sales-history?q=${encodeURIComponent(this.card.ebayQuery)}`;
    },
  },

  methods: {
    async chooseGradedCardProfile() {
      if (!this.saving) {
        this.$emit('changeDisplay', 'loading');

        this.certGraderInput = this.card.grader || 'psa';
        this.certInput = this.card.cert || '';

        if (this.card.profileId && this.card.grade && this.card.grader) {
          this.submitGradedCardAutomatic();
        } else if (this.card.cert && this.card.grader) {
          this.fetchGradedCardInfo();
        } else {
          this.$emit('changeDisplay', 'valueGradedCardAutomatic');
        }
      }
    },

    async choosePlayerIndex() {
      if (!this.saving) {
        this.$emit('changeDisplay', 'loading');
        await this.fetchPlayer(this.card.player);

        if (this.player) {
          this.submitPlayerIndex(this.card.player);
        } else {
          this.$emit('changeDisplay', 'valuePlayer');
        }
      }
    },

    chooseMultipliers() {
      if (!this.saving) {
        this.$emit('changeDisplay', 'valueMultipliers');
      }
    },

    async chooseLastSale() {
      if (!this.saving) {
        this.$emit('changeDisplay', 'loading');

        if (this.card.otherIndexId) {
          await this.setIndex(this.card.otherIndexId);
        } else {
          await this.fetchPlayer(this.card.playerIndexId || this.card.player);
        }

        if (this.card.ebayQuery) {
          this.fetchSales();
        }

        if (this.card.useLastSoldValue) {
          this.lastSoldOptionsValue = 'lastsold';
        }

        this.$emit('changeDisplay', 'valueLastSale');
      }
    },

    async chooseAdvanced() {
      if (!this.saving) {
        this.$emit('changeDisplay', 'loading');

        if (this.card.otherIndexId) {
          await this.setIndex(this.card.otherIndexId);
        } else {
          await this.fetchPlayer(this.card.playerIndexId || this.card.player);
        }

        this.$emit('changeDisplay', 'valueAdvanced');
      }
    },

    chooseManual() {
      if (!this.saving) {
        this.$emit('changeDisplay', 'valueManual');
      }
    },

    async fetchPlayer(id) {
      this.player = await playerService.getPlayer(id);
      this.index = null;
      this.$set(this.card, 'playerIndexId', this.player ? this.player.id : null);
      this.$set(this.card, 'otherIndexId', null);
    },

    async fetchSales() {
      if (!this.card.ebayQuery) return;

      this.loadingSales = true;

      try {
        const data = await searchService.search({
          index: 'salesarchive',
          query: this.card.ebayQuery,
          page: 0,
          limit: 10,
          sort: 'date',
        });

        this.sales = data.hits;
      } catch (e) {
        Sentry.captureException(e);
      }

      this.loadingSales = false;
    },

    async setIndex(id) {
      this.index = await indexService.getIndex('indexes', id);
      this.player = null;
      this.$set(this.card, 'playerIndexId', null);
      this.$set(this.card, 'otherIndexId', id);
    },

    async setPlayer({ id }) {
      if (this.display === 'valuePlayer') {
        this.submitPlayerIndex(id);
      } else {
        await this.fetchPlayer(id);
      }
    },

    async fetchGradedCardInfo() {
      this.saving = true;

      try {
        const { data } = await firebase.functions().httpsCallable('httpcertlookup')({ cert: this.certInput, grader: this.certGraderInput });

        if (data.profileId && data.grade && data.grader) {
          this.$set(this.card, 'profileId', data.profileId);
          this.$set(this.card, 'grade', data.grade);
          this.$set(this.card, 'grader', data.grader);
          this.$set(this.card, 'cert', this.certInput);
          this.$set(this.card, 'hasQualifier', Boolean(data.qualifierType));
          this.$set(this.card, 'qualifierType', data.qualifierType || null);
          this.$set(this.card, 'hasAutographGrade', Boolean(data.autographGrade));
          this.$set(this.card, 'autographGrade', data.autographGrade || null);

          this.submitGradedCardAutomatic();
        } else {
          this.$emit('changeDisplay', 'valueGradedCardAutomatic');
        }
      } catch (e) {
        this.$toasted.error('Error finding information on this card');
        Sentry.captureException(e);
        this.$emit('changeDisplay', 'valueGradedCardAutomatic');
      }
    },

    async submitGradedCardAutomatic() {
      this.$set(this.card, 'multipliers', []);
      this.$set(this.card, 'playerIndexId', null);
      this.$set(this.card, 'otherIndexId', null);
      this.$set(this.card, 'playerIndexPrice', null);
      this.$set(this.card, 'playerIndexDate', null);
      this.$set(this.card, 'useSalesForValue', false);
      this.$set(this.card, 'useGradedCardAutomatic', true);
      this.$set(this.card, 'dailySales', {});
      this.$set(this.card, 'ebayQuery', '');
      this.$set(this.card, 'grader', this.certGraderInput);

      await this.saveCollectionCard('Card value estimate set!');
    },

    async submitPlayerIndex(id) {
      this.$set(this.card, 'multipliers', []);
      this.$set(this.card, 'playerIndexId', id);
      this.$set(this.card, 'otherIndexId', null);
      this.$set(this.card, 'playerIndexPrice', null);
      this.$set(this.card, 'playerIndexDate', null);
      this.$set(this.card, 'useSalesForValue', false);
      this.$set(this.card, 'useGradedCardAutomatic', false);
      this.$set(this.card, 'dailySales', {});

      await this.saveCollectionCard('Card value tracking set up!');
    },

    async submitMultipliers() {
      this.$set(this.card, 'playerIndexId', null);
      this.$set(this.card, 'otherIndexId', null);
      this.$set(this.card, 'playerIndexPrice', null);
      this.$set(this.card, 'playerIndexDate', null);
      this.$set(this.card, 'useSalesForValue', false);
      this.$set(this.card, 'useGradedCardAutomatic', false);
      this.$set(this.card, 'dailySales', {});

      await this.saveCollectionCard('Card value tracking set up!');
    },

    async submitAdvanced() {
      this.$set(this.card, 'multipliers', []);
      this.$set(this.card, 'useSalesForValue', false);
      this.$set(this.card, 'useGradedCardAutomatic', false);
      this.$set(this.card, 'dailySales', {});

      await this.saveCollectionCard('Card value tracking set up!');
    },

    async submitLastSale() {
      this.$set(this.card, 'multipliers', []);
      this.$set(this.card, 'playerIndexPrice', null);
      this.$set(this.card, 'playerIndexDate', null);
      this.$set(this.card, 'useSalesForValue', true);
      this.$set(this.card, 'useGradedCardAutomatic', false);
      this.$set(this.card, 'useLastSoldValue', this.lastSoldOptionsValue !== 'index');
      this.$set(this.card, 'dailySales', {});

      if (!this.card.stats) {
        this.$set(this.card, 'stats', {});
      }

      this.$set(this.card.stats, 'newestSale', { date: this.sales[0].date, price: this.dailySales[moment(this.sales[0].date).utcOffset(-7).format('MM/DD/YYYY')].p });

      await this.saveCollectionCard('Give this a few seconds and then refresh to view sales and your new value!');
    },

    async submitManual() {
      this.$set(this.card, 'multipliers', []);
      this.$set(this.card, 'playerIndexId', null);
      this.$set(this.card, 'otherIndexId', null);
      this.$set(this.card, 'playerIndexPrice', null);
      this.$set(this.card, 'playerIndexDate', null);
      this.$set(this.card, 'useSalesForValue', false);
      this.$set(this.card, 'useGradedCardAutomatic', false);
      this.$set(this.card, 'dailySales', {});

      await this.saveCollectionCard('Card value estimate set!');
    },

    async saveCollectionCard(successMessage) {
      this.saving = true;

      try {
        const collectionCard = await getCollectionCardToSave(
          this.card,
          cardService,
          indexService,
          firebase,
          this.collectionId,
          this.user.uid,
        );

        await authService.createOrUpdateCollectionCard(
          this.user.uid,
          this.collectionId,
          collectionCard,
        );

        this.$toasted.success(successMessage);
        this.$bus.$emit('card-profile-changed');
        this.$bus.$emit('collection-changed', { edit: { id: this.card.collectionCardId, ...collectionCard } });
        this.$emit('close');
      } catch (e) {
        this.$toasted.error('Error saving the card');
        Sentry.captureException(e);
      }

      this.saving = false;
    },

    handleSaleSelected(sale) {
      this.$set(this.card, 'playerIndexPrice', sale.price);
      this.$set(this.card, 'playerIndexDate', sale.date);
    },

    selectEstimateOption(option) {
      this[option.onSelect]();
    },

    goBack() {
      this.sales = [];
      this.lastSoldOptionsValue = 'index';
      this.certGraderInput = 'psa';
      this.$emit('changeDisplay', 'valueIntro');
    },
  },
};
</script>

<style lang="scss" scoped>
.intro {
  text-align: center;
}

h4 {
  margin-bottom: $gp * .5;
}

.cta-options {
  display: grid;
  gap: $gp;
  margin: $gp * 2 auto;
  grid-template-columns: repeat(2, 1fr);
  max-width: 800px;

  @media($small-down) {
    grid-template-columns: repeat(1, 1fr);
  }

  > div {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .external-link {
    display: block;
    margin-top: $gp * .5;
  }
}

.grid {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: 3fr 2fr;
  margin-top: $gp;

  @media($medium-down) {
    display: block;

    > section:first-child {
      margin-bottom: $gp * 2;
    }
  }
}

.form {
  max-width: 600px;
  margin: $gp auto;
}

.advanced,
.lastsale {
  max-width: 1000px;
  margin: $gp auto;
}

.value-estimate {
  margin-top: $gp;

  .description {
    margin-top: $gp * .5;
  }
}

.checks {
  color: $color-gray-5;

  &.success {
    color: $color-green-1;
  }

  > * {
    margin-bottom: $gp;
  }

  h5 {
    margin-bottom: 0;
  }
}

.index-list-container {
  margin: 0 (-$gp);
}

.sales-container {
  margin-bottom: $gp;
}

.results-header {
  margin-bottom: $gp;

  h5 {
    margin-bottom: 0;
  }
}

.loading-container {
  padding: $gp;
  text-align: center;
}
</style>
